/** Spans of the recording that have been heard, in ms. */
export type Interval = readonly [number, number];

export type Line = { startMs: number; endMs: number; text: string };

export const BACK_MS = 15_000;
export const SPEEDS = [1.0, 0.85] as const;
export type Speed = (typeof SPEEDS)[number];

/** Completion is counted in the client at 90 % (lesson library PRD §4.2). */
const COMPLETE_AT = 0.9;

/**
 * Add a heard span, merged into the rest. Merging is what makes seeking back
 * over audio already heard add nothing: the fraction is distinct time, not
 * time elapsed.
 */
export function addInterval(heard: readonly Interval[], from: number, to: number): Interval[] {
  if (!(to > from)) return [...heard];
  const out: Interval[] = [];
  let lo = from;
  let hi = to;
  for (const [a, b] of [...heard].sort((x, y) => x[0] - y[0])) {
    if (b < lo || a > hi) out.push([a, b]);
    else {
      lo = Math.min(lo, a);
      hi = Math.max(hi, b);
    }
  }
  out.push([lo, hi]);
  return out.sort((x, y) => x[0] - y[0]);
}

export function heardFraction(heard: readonly Interval[], durationMs: number): number {
  if (!(durationMs > 0)) return 0;
  const total = heard.reduce((s, [a, b]) => s + Math.max(0, Math.min(b, durationMs) - Math.max(a, 0)), 0);
  return Math.min(1, total / durationMs);
}

export function isComplete(fraction: number): boolean {
  return fraction >= COMPLETE_AT;
}

export function seekBack(currentMs: number): number {
  return Math.max(0, currentMs - BACK_MS);
}

export function nextSpeed(s: Speed): Speed {
  return s === 1.0 ? 0.85 : 1.0;
}

/** Index of the line being spoken; in a gap, the last one that started. */
export function lineAt(lines: readonly Line[], ms: number): number | null {
  let at: number | null = null;
  for (let i = 0; i < lines.length; i++) {
    if ((lines[i] as Line).startMs <= ms) at = i;
    else break;
  }
  return at;
}

/**
 * How long the transcript stops following after the learner scrolls it. A
 * pane that snapped back on the next line, about every three seconds, would
 * take a line away from someone reading back over it; one that never resumed
 * would need a button to undo the scroll. A pause is neither.
 */
export const FOLLOW_PAUSE_MS = 4_000;

export function follows(nowMs: number, scrolledAtMs: number | null): boolean {
  return scrolledAtMs === null || nowMs - scrolledAtMs >= FOLLOW_PAUSE_MS;
}

/**
 * Where to scroll the transcript so the line being spoken sits a third of the
 * way down: high enough that the next lines show below it, low enough that
 * the one just heard is still above. `top` is the line's offset inside the
 * pane's content.
 */
export function followScrollTop(
  line: { top: number; height: number },
  pane: { clientHeight: number; scrollHeight: number },
): number {
  const want = line.top + line.height / 2 - pane.clientHeight / 3;
  return Math.max(0, Math.min(want, pane.scrollHeight - pane.clientHeight));
}

/**
 * When to try `finishLessonPlay` again after it failed: 5 s, then 10 s, then
 * 15 s, then never. The completion check runs on every `timeupdate`, so a
 * server that keeps refusing would otherwise be asked about four times a
 * second for the rest of the recording.
 */
export function finishRetryDelayMs(failures: number): number | null {
  return failures >= 1 && failures <= 3 ? 5_000 * failures : null;
}
